// English strings for email delivery settings (PB-13-04; email stream PD-580 – PD-589). Arabic:
// ar-email.ts.
export const enEmail = {
  // ---------------------------------------------------------------- API messages and validation
  'eml.api.notAllowed': 'This organisation is not allowed to use its own email sender. A system administrator can allow it in the system console.',
  'eml.v.host': 'Enter a server host name or IP address, without "smtp://" or a port.',
  'eml.v.domain': 'Enter a domain such as example.com.',
  'eml.v.envName': 'Use a variable name that starts with PASSAGE_SMTP_ (capital letters, digits and _ only).',
  'eml.problem.from_missing': 'Enter the address messages are sent from.',
  'eml.problem.host_missing': 'Enter the SMTP server host.',
  'eml.problem.port_missing': 'Enter the SMTP server port (usually 587 for STARTTLS or 465 for TLS).',
  'eml.problem.security_missing': 'Choose how the connection is encrypted.',
  'eml.problem.insecure_transport': 'An unencrypted connection is only allowed to a local test server outside production. Choose TLS or STARTTLS.',
  'eml.problem.credentials_incomplete': 'Enter both a user name and a password, or neither.',
  'eml.problem.domain_missing': 'Enter the domain your organisation sends from.',
  'eml.problem.from_domain_mismatch': 'The sender address must be on your sending domain (or one of its subdomains).',
  'eml.problem.development_domain': 'Use a real domain you own: development and example domains (localhost, .test, example.com …) are refused on this deployment.',
  'eml.problem.environment_reference_not_allowed': 'An organisation stores its password here; environment references are for the installation default only.',

  // ---------------------------------------------------------------- test message (sent in both languages)
  'eml.test.subject': 'Passage test email',
  'eml.test.body': 'This is a test message from Passage to check the email sender settings. No action is needed.',

  // ---------------------------------------------------------------- shared form
  'eml.nav': 'Email delivery',
  'eml.provider': 'Provider',
  'eml.provider.development_mailbox': 'Development mailbox (default)',
  'eml.provider.smtp': 'SMTP server',
  'eml.provider.none': 'Off',
  'eml.provider.development_mailbox.hint': 'Messages are written to a local folder on the server and never leave it. For development and evaluation only.',
  'eml.provider.smtp.hint': 'Any provider that offers SMTP submission: Amazon SES, Microsoft 365, Google Workspace, SendGrid, Postmark or your own relay.',
  'eml.provider.none.hint': 'No email is sent. Every email is recorded as unavailable.',
  'eml.section.sender': 'Sender',
  'eml.section.smtp': 'SMTP connection',
  'eml.fromAddress': 'From address',
  'eml.fromAddress.hint': 'The address recipients see and the return path for bounces.',
  'eml.fromNameEn': 'From name (English)',
  'eml.fromNameAr': 'From name (Arabic)',
  'eml.fromName.hint': 'Shown to recipients next to the address; Arabic messages use the Arabic name when there is one.',
  'eml.replyTo': 'Reply-to address',
  'eml.replyTo.hint': 'Optional. Where replies go.',
  'eml.smtpHost': 'Server host',
  'eml.smtpPort': 'Port',
  'eml.smtpSecurity': 'Encryption',
  'eml.security.tls': 'TLS from the start (usually port 465)',
  'eml.security.starttls': 'STARTTLS required (usually port 587)',
  'eml.security.none': 'None (local test server only)',
  'eml.security.hint': 'The server certificate is always verified.',
  'eml.smtpUsername': 'User name',
  'eml.password': 'Password',
  'eml.password.state.none': 'No password is set.',
  'eml.password.state.stored': 'A password is stored (encrypted). It cannot be shown.',
  'eml.password.state.environment': 'The password is read from the server environment variable {name} (or {name}_FILE).',
  'eml.password.changed': 'Last changed {at}.',
  'eml.password.action': 'Password change',
  'eml.password.keep': 'Keep the current password',
  'eml.password.set': 'Enter a new password',
  'eml.password.clear': 'Remove the password',
  'eml.password.environment': 'Read it from a server environment variable',
  'eml.password.new': 'New password',
  'eml.password.new.hint': 'Write-only: it is stored encrypted and never shown again.',
  'eml.password.envName': 'Variable name',
  'eml.password.envName.hint': 'Must start with PASSAGE_SMTP_. The server operator sets it (or {name}_FILE) for the API and the worker.',
  'eml.version': 'Settings version {version}, saved {at}.',
  'eml.problems.title': 'These settings cannot send yet',
  'eml.saved': 'Email settings saved.',

  // ---------------------------------------------------------------- test email
  'eml.test.title': 'Send a test email',
  'eml.test.intro': 'Sends a short bilingual test message with the saved settings. Save your changes first.',
  'eml.test.to': 'Send to',
  'eml.test.send': 'Send test email',
  'eml.test.sending': 'Sending…',
  'eml.test.note': '"Accepted" means the server took the message. It does not mean it was delivered or read.',
  'eml.test.outcome.accepted': 'Accepted by the server',
  'eml.test.outcome.refused': 'Refused by the server',
  'eml.test.outcome.deferred': 'Temporarily refused by the server — try again later',
  'eml.test.outcome.not_connected': 'Could not reach the server — nothing was sent',
  'eml.test.outcome.unknown': 'Unknown — the server did not answer in time after the message was handed over',
  'eml.test.outcome.unavailable': 'Not sent — the settings cannot be used',
  'eml.test.code': 'Server reply code {code}',
  'eml.test.detail': 'Details: {detail}',
  'eml.test.version': 'Tested settings version {version}.',
  'eml.lastTest': 'Last test {at}: {outcome}',
  'eml.lastTest.none': 'No test email has been sent yet.',
  'eml.detail.email_off': 'Email is switched off.',
  'eml.detail.destination_refused': 'This server address is not allowed (for example a private or link-local network address).',
  'eml.detail.password_missing': 'No password is stored.',
  'eml.detail.secret_keys_missing': 'The server has no key ring to read the stored password (MAIL_SECRET_KEYS).',
  'eml.detail.password_unreadable': 'The stored password cannot be read with the current key ring. Enter it again.',
  'eml.detail.password_reference_invalid': 'The environment variable name is not allowed.',
  'eml.detail.password_reference_missing': 'The environment variable is not set on this server.',
  'eml.detail.password_reference_unreadable': 'The environment variable file cannot be read on this server.',
  'eml.detail.timeout': 'The server did not answer in time.',

  // ---------------------------------------------------------------- system console
  'eml.platform.title': 'Email delivery',
  'eml.platform.intro': 'The sender and provider used for all organisations unless an organisation is allowed to send as itself. Changes apply to the next message; no restart is needed.',
  'eml.platform.secretKeys': 'Stored passwords are encrypted with the installation key ring.',
  'eml.platform.org.title': 'Email sender',
  'eml.platform.org.sub': 'Whether this organisation may send email from its own domain and SMTP server. Its administrators configure and test it.',
  'eml.platform.org.allow': 'Allow this organisation to use its own sender',
  'eml.platform.org.reason': 'Reason for the change',
  'eml.platform.org.apply': 'Apply',
  'eml.platform.org.notConfigured': 'Not configured by the organisation yet.',
  'eml.inEffect.organisation': 'In use: the organisation’s own sender',
  'eml.inEffect.installation': 'In use: the installation default sender',

  // ---------------------------------------------------------------- organisation (Communications)
  'eml.org.tab': 'Email sender',
  'eml.org.title': 'Your organisation’s email sender',
  'eml.org.intro': 'By default your messages use the installation’s sender. If a system administrator allowed it, you can send from your own domain through your own SMTP server.',
  'eml.org.notAllowed': 'Your organisation uses the installation sender. To send from your own domain, ask a system administrator to allow it.',
  'eml.org.installationSender': 'Installation sender: {sender}',
  'eml.org.installationSender.none': 'Installation sender: not set (development mailbox)',
  'eml.org.enabled': 'Use our own sender for our messages',
  'eml.org.enabled.hint': 'Takes effect once a test email with the current settings was accepted by the server.',
  'eml.org.sendingDomain': 'Sending domain',
  'eml.org.sendingDomain.hint': 'The domain you send from. The sender address must be on it. Configure SPF and DKIM for it with your provider.',
  'eml.org.verified': 'A test email with these settings was accepted by the server.',
  'eml.org.notVerified': 'Send a test email to confirm these settings. Until then the installation sender is used.',

  // ---------------------------------------------------------------- delivery log statuses (PD-584)
  'comms.status.refused': 'Refused',
  'comms.status.unknown': 'Unknown',
  'inv.msg.refused': 'Email refused by mail server',
  'inv.msg.unknown': 'Email outcome unknown (server did not confirm)',
} as const;
